import { isNull, isUndefined } from 'lodash';
import { POST_STATUS } from '../constants';
import { icuOptions } from '../constants/icu-options';
import createI18nConfig from './create-i18n-config';
import { getTimeAgoFormat, isLessThanMinute } from './time-ago';
import { getTimezoneDate } from './timezone';
let _rtfFormatter;
let _language;
export const formatDate = ({ date, lng, timezone }) => {
    const format = getTimeAgoFormat(date);
    let value = date;
    if (format !== 'timeRelative' && timezone) {
        value = getTimezoneDate(date, timezone);
    }
    return createI18nConfig().interpolation.format(value, format, lng);
};
export const formatDateICU = ({ date, lng, rtfFormatter, timezone, }) => {
    const format = getTimeAgoFormat(date);
    if ((!_rtfFormatter || lng !== _language) && rtfFormatter) {
        _rtfFormatter = rtfFormatter;
        _language = lng;
    }
    if (format === 'timeRelative' && _rtfFormatter) {
        return isLessThanMinute(date)
            ? 'few-seconds-ago'
            : _rtfFormatter(new Date(date));
    }
    let value = date;
    if (format !== 'timeRelative' && timezone) {
        value = getTimezoneDate(date, timezone);
    }
    const options = format === 'date' || format === 'fullDate'
        ? icuOptions.formats.date[format]
        : undefined;
    // @ts-expect-error
    return new Intl.DateTimeFormat(lng, options).format(new Date(value));
};
export const formatNumber = (number, lng) => {
    return createI18nConfig().interpolation.format(number, 'number', lng);
};
export const formatNumberICU = (number, lng) => {
    return new Intl.NumberFormat(lng).format(number);
};
const getPostDateFieldFormatters = (useICU = false) => [
    {
        fieldIn: 'firstPublishedDate',
        fieldOut: 'firstPublishedDateFormatted',
        formatter: useICU ? formatDateICU : formatDate,
    },
];
const getPostFieldFormatters = (useICU = false) => [
    {
        fieldIn: 'viewCount',
        fieldOut: 'viewCountFormatted',
        formatter: useICU ? formatNumberICU : formatNumber,
    },
    {
        fieldIn: 'likeCount',
        fieldOut: 'likeCountFormatted',
        formatter: useICU ? formatNumberICU : formatNumber,
    },
    {
        fieldIn: 'totalComments',
        fieldOut: 'totalCommentsFormatted',
        formatter: useICU ? formatNumberICU : formatNumber,
    },
];
/**
 * Mutates the post by adding formatted post props:
 * - `firstPublishedDateFormatted`
 * - `viewCountFormatted`
 * - `likeCountFormatted`
 * - `totalCommentsFormatted`
 *
 * @public Used by `blog-node`
 **/
export const localizePost = ({ post, language, useICU = false, rtfFormatter = null, timezone = null, }) => {
    if ((!_rtfFormatter || language !== _language) && rtfFormatter) {
        _rtfFormatter = rtfFormatter;
        _language = language;
    }
    getPostFieldFormatters(useICU).forEach(({ fieldIn, fieldOut, formatter }) => {
        if (isUndefined(post[fieldOut])) {
            const value = post[fieldIn];
            if (isNull(value) || isUndefined(value)) {
                post[fieldOut] = null;
            }
            else {
                post[fieldOut] = formatter(value, language);
            }
        }
    });
    getPostDateFieldFormatters(useICU).forEach(({ fieldIn, fieldOut, formatter }) => {
        if (isUndefined(post[fieldOut])) {
            const value = post[fieldIn];
            if (isNull(value) || isUndefined(value)) {
                post[fieldOut] = null;
            }
            else {
                post[fieldOut] = formatter({ date: value, lng: language, timezone }, language);
            }
        }
    });
    if (isUndefined(post.dateByStatus)) {
        post.dateByStatus = localizeDateByStatus(post, language, useICU);
    }
};
export const localizeLikeCount = (post, language) => {
    post.likeCountFormatted = formatNumberICU(post.likeCount, language);
};
export function localizeDateByStatus(post, language, useICU) {
    const date = getDateByStatus(post);
    if (isUndefined(date) || isNull(date)) {
        return null;
    }
    return useICU
        ? formatDateICU({ date, lng: language, rtfFormatter: _rtfFormatter })
        : formatDate({ date, lng: language });
}
function getDateByStatus(post) {
    switch (post.status) {
        case POST_STATUS.published:
            return post.firstPublishedDate;
        case POST_STATUS.unpublished:
        case POST_STATUS.in_review:
            return post.draft?.editedDate;
        default:
            return null;
    }
}
//# sourceMappingURL=localization.js.map